import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './cn';

/**
 * Token-driven button primitive (PRO-0260, DS-7/DS-11/DS-12). Variants via cva;
 * ≥44px min target (`min-h-11`); token colours; visible token `:focus-visible`
 * ring (never suppressed). Defaults to `type="button"` so it is never an accidental
 * form submit.
 */
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 min-h-11 rounded-md font-semibold text-base ' +
    'transition-colors ease-ds cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 ' +
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary-hover',
        secondary: 'bg-surface text-text-primary border border-border hover:bg-muted',
        ghost: 'bg-transparent text-text-primary hover:bg-muted',
        destructive: 'bg-danger text-danger-foreground hover:opacity-90',
      },
      size: {
        md: 'px-4 py-2',
        sm: 'px-3 py-1.5',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, type, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
});
